<template>

    <div class="bg-white h-64 flex-none shadow z-top">
        <nav class="w-full h-full px-24 flex items-center">
            <template v-if="!disabled">
                <div class="flex gap-16">
                    <simple-button
                        v-if="hasDemoData"
                        display="danger"
                        look="primary"
                        size="small"
                        @click="startDeletingDemoData"
                    >
                        {{ $t('companypage.clear_demo_data') }}
                    </simple-button>

                    <simple-button
                        href="https://jobtoolz.referral-factory.com/aJuFtO/join"
                        target="_blank"
                        icon="person-add"
                        size="small"
                        type="link"
                    >
                        {{ $t('companypage.refer_jobtoolz') }}
                    </simple-button>
                </div>

                <div v-if="currentCompany.free_trial && trialDaysLeft !== false"
                     :class="[hasDemoData ? 'ml-16' : '', trialDaysLeft <= 3 ? 'text-semantic-warning' : '']"
                     class="font-semibold rounded py-8 px-12 transition duration-150 flex items-center">
                    <svg-icon
                        :class="trialDaysLeft <= 3 ? '' : 'text-secondary'"
                        class="inline mr-4"
                        type="schedule"
                    />
                    <template v-if="trialDaysLeft === 0">
                        {{ $t('companypage.trial_expires.today') }}
                    </template>
                    <template v-else-if="trialDaysLeft === 1">
                        {{ $t('companypage.trial_expires.tomorrow') }}
                    </template>
                    <template v-else>
                        <span
                            v-html="$t('companypage.trial_expires.later' + (trialDaysLeft <= 3 ? '_no_color' : ''), { days: trialDaysLeft })"></span>
                    </template>
                </div>

                <popup-modal-confirm v-if="deleteDemoData" :message="$t('companypage.confirm_deletion_demo_data')"
                                     :working="performingRequest" @close="stopDeletingDemoData" @confirm="performDeleteRequest"/>

                <div class="mr-auto"></div>

                <template v-if="companyActive">
                    <div
                        class="flex space-x-24"
                        :class="canAdmin ? 'mr-24' : ''"
                    >
                        <component
                            v-for="(item, key) in menuItems"
                            :key="key"
                            :is="item.component"
                            :icon="item.icon"
                            :tooltip="capitalize(item.tooltip)"
                            :fetch="key === 'alerts'"
                            :class="item.classes"
                        />
                    </div>
                </template>

                <a v-if="canAdmin"
                   class="text-primary hover:text-secondary flex items-center"
                   href="/admin"
                   title="Admin Settings">
                    <svg-icon size="16" type="settings"></svg-icon>
                </a>

                <user-menu :class="companyActive ? 'border-r border-l border-primary-20 px-16 ml-32 mr-16' : ''" class="flex-none"></user-menu>

                <a v-if="companyActive" :href="jobsiteLink" class="text-primary flex items-center" target="_blank">

                    <svg-icon class="fill-current mr-8" type="open-in-new"/>

                    <span class="font-semibold">{{ $t('companypage.minisite') }}</span>
                </a>
            </template>
            <template v-else>
                <div class="ml-auto mr-72">
                    <div class="flex items-center">
                        <div class="flex-none rounded-32 overflow-hidden mr-14" style="width: 32px; height: 32px;">
                            <div class="bg-primary-20 shadow-inner w-full h-full"></div>
                        </div>
                        <div class="bg-primary-20 h-24 w-128 rounded"></div>
                    </div>
                </div>
            </template>

        </nav>
    </div>

</template>

<script>
import SearchBar from './SearchBar.vue';
import UserMenu from './UserMenu.vue';
import SvgIcon from '../ui/SvgIcon.vue';
import Alerts from '../ui/Alerts.vue';
import Gimmicks from '../ui/Gimmicks.vue';
import PopupModalConfirm from '../ui/PopupModalConfirm';
import * as companies from '../../services/CompanyService';
import Company from '../../models/Company';
import Job from '../../models/Job';
import Application from '../../models/Application';
import {normalizeJson} from '../../../shared/helpers';
import Tasks from '../ui/Tasks.vue';
import moment from 'moment';
import Feature from "../Feature.vue";
import capitalize from "../../mixins/capitalize";
import SimpleButton from "../ui/SimpleButton.vue";
import {mapStores, mapActions} from "pinia";
import {useMainStore} from "../../state/store";
import {useJobsStore} from "../../state/modules/jobs";
import {useApplicationsStore} from "../../state/modules/applications";
import {useRepo} from "pinia-orm";
import {markRaw} from "vue";

export default {
    components: {
        SimpleButton,
        'search-bar': SearchBar,
        'user-menu': UserMenu,
        'svg-icon': SvgIcon,
        Alerts,
        Gimmicks,
        Tasks,
        PopupModalConfirm,
        Feature
    },
    mixins: [capitalize],
    props: {
        disabled: Boolean,
    },
    data: function () {
        return {
            deleteDemoData: false,
            performingRequest: false,
            menuItems: {
                tasks: {
                    component: markRaw(Tasks),
                    icon: 'task-alt',
                    tooltip: this.$t('tasks.tasks'),
                    classes: '',
                },
                gimmicks: {
                    component: markRaw(Gimmicks),
                    icon: 'newsmode',
                    tooltip: this.$t('general.new_in_jobtoolz'),
                    classes: '',
                },
                alerts: {
                    component: markRaw(Alerts),
                    icon: 'notifications',
                    tooltip: null,
                    classes: 'relative',
                },
            },
            hasDemoData: false,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        currentUser() {
            return this.mainStore.currentUser;
        },
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        jobsiteLink() {
            let locale = this.currentCompany.jobsite.active_languages.includes(this.mainStore.locale)
                ? this.mainStore.locale
                : this.currentCompany.jobsite.active_languages[0];

            return `${this.currentCompany.jobsite_url}/${locale}`;
        },
        canAdmin() {
            return this.currentUser && this.currentUser.admin;
        },
        companyActive() {
            return this.currentCompany && this.currentCompany.active;
        },
        trialDaysLeft() {
            const end = moment(this.currentCompany.trial_end).endOf('day');

            if (end.isBefore(moment().endOf('day'))) {
                return false;
            }

            return end.diff(moment(), 'days');
        },
    },
    methods: {
        ...mapActions(useJobsStore, ['clear']),
        ...mapActions(useApplicationsStore, ['resetState']),
        startDeletingDemoData() {
            this.deleteDemoData = true;
        },
        stopDeletingDemoData() {
            this.deleteDemoData = false;
        },
        performDeleteRequest() {
            this.performingRequest = true,

                companies.update(this.currentCompany.id, {
                    data: {
                        attributes: {
                            hasDemoData: false,
                        },
                    },
                }).then(result => {
                    return useRepo(Company)
                        .whereId(result.id)
                        .update(normalizeJson(result));
                }).then(() => {
                    useRepo(Job).flush();
                    this.clear();

                    useRepo(Application).flush();
                    this.resetState();

                    this.stopDeletingDemoData();
                    this.performingRequest = false;
                    this.hasDemoData = false;
                });
        },
        fetchHasDemoData() {
            // Fetch the company data to check if the company has demo data
            companies.hasDemoData(this.mainStore.currentCompany.id).then(result => {
                this.hasDemoData = result.hasDemoData;
            });
        }
    },
    beforeMount() {
        this.fetchHasDemoData();
    }
};
</script>
